<template>
  <div id="goal-record" class="card">
    <header class="card-header">
      <span class="card-header-title">
        Daily Goal Record
      </span>
      <div class="field card-header-icon">
        <b-switch v-model="showNonReportingDaysPreference" size="is-small">
          Show non-reporting days.
        </b-switch>
      </div>
    </header>
    <div class="card-content">
      <div v-if="isLoading" style="position: relative; min-height: 150px">
        <b-loading v-if="isLoading" :is-full-page="false" :active.sync="isLoading" />
      </div>
      <b-table
        v-else
        :data="matchingGoalsArray"
        hoverable
        :mobile-cards="true"
        :row-class="(row, index) => getRowClasses(row, index)"
        class="goal-record__table"
      >
        <template
          slot-scope="props"
          v-if="isReportingDay(props.row, props.index) || areNonReportingDaysShowing"
        >
          <b-table-column
            field="id"
            label="Date"
            width="162"
            :class="{
              'has-text-grey-light': !isReportingDay(props.row, props.index)
            }"
          >
            {{ props.row
                ? new Date(parseDate(props.row.goalDate)).toLocaleString('en-US', dateOptions)
                : new Date(parseDate(dates[props.index])).toLocaleString('en-US', dateOptions)
            }}
            <div
              v-if="!isReportingDay(props.row, props.index)"
              class="has-text-danger is-size-7"
            >
              (No standup today)
            </div>
          </b-table-column>

          <b-table-column
            field="first_name"
            label="Main Goal"
            :class="{ 'has-text-grey-light': !props.row }"
          >
            <goal-text-input
              :active-goal-id="props.row ? props.row.id : undefined"
              :active-goal-date="props.row ? props.row.goalDate : dates[props.index]"
              :active-user-id="activeUser.id"
              size="small"
            />
            <b-collapse
              v-if="props.row && props.row.goalStatusExplanation && activeWorkspace.promptForGoalExplanationPreference"
              :open="false"
              position="is-bottom"
            >
              <a
                class="has-text-grey is-size-6"
                slot="trigger"
                slot-scope="props"
                aria-controls="explanation-content"
              >
                <b-icon size="is-small" :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
                {{ !props.open ? 'More' : 'Less' }}
              </a>
              <p>
                {{ props.row.goalStatusExplanation }} |

                <a
                  class="has-text-primary"
                  style="text-decoration: underline;"
                  @click="isGoalDetailModalOpen = props.row.id"
                  @keyup.enter="isGoalDetailModalOpen = props.row.id"
                >
                  Edit
                </a>
              </p>
            </b-collapse>

            <goal-status-detail-modal
              :is-modal-open="!!props.row && isGoalDetailModalOpen == props.row.id"
              :goal-id="props.row ? props.row.id : undefined"
              :goal-status="props.row ? props.row.goalStatus : undefined"
              :goal-status-detail-text="props.row ? props.row.goalStatusExplanation : undefined"
              @modal-close="handleUpdateGoalStatus"
            />
          </b-table-column>

          <b-table-column field="date" label="Status" width="134">
            <goal-status-icon-group :goalId="props.row ? props.row.id : null" />
          </b-table-column>
        </template>
      </b-table>
    </div>
    <div v-if="!isLoading" class="card-footer">
      <div class="card-footer-item">
        <b-button type="is-primary" :loading="isNextPageLoading" @click="handleLoadMoreGoals">
          Load More
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as UserActions from '@/store/actions/User.actions';
import * as NotificationService from '@/services/Notification.service';
import * as notificationTypes from '@/constants/notificationTypes';
import {
  generatePastDatesInISO,
  shortDateOptions,
  isSameDate,
  dateToISO,
  parseDate,
  isSameDayOfTheWeek,
} from '@/helpers/dateHelpers';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import * as Types from '@/constants/Types';
import * as TimeAndDateConstants from '@/constants/TimeAndDateConstants';
import GoalStatusIconGroup from '@/components/GoalStatusIconGroup.vue';
import GoalTextInput from '@/components/GoalTextInput.vue';
import GoalStatusDetailModal from '@/components/GoalStatusDetailModal.vue';

export default {
  name: 'GoalRecord',

  components: {
    GoalStatusIconGroup,
    GoalTextInput,
    GoalStatusDetailModal,
  },

  props: {
    activeUser: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      numberOfPages: 1,
      pageSize: 10,
      isNextPageLoading: false,
      dateOptions: shortDateOptions,
      areNonReportingDaysShowing: this.activeUser.showNonReportingDays,
      areUserGoalsLoading: true,
      Types,
      isGoalDetailModalOpen: false,
    };
  },

  computed: {
    ...mapState({
      goals: state => state.goal.goals,
      teams: state => state.team.teams,
      users: state => state.user.users,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUserTeams',
    ]),
    dates() {
      return this.generatePastDatesInISO(this.pageSize * this.numberOfPages);
    },
    goalsAsArray() {
      if (this.isObjectEmpty(this.goals)) return [];

      return Object.values(this.goals);
    },
    activeUserGoals() {
      return this.goalsAsArray.filter(goal => goal.userId === this.activeUser.id);
    },
    matchingGoalsArray() {
      const matchingDates = this.dates
        .map(date => this.activeUserGoals.find(goal => this.isSameDate(goal.goalDate, date)) || null);

      return !this.isNextPageLoading ? matchingDates : matchingDates.slice(0, matchingDates.length - this.pageSize);
    },
    loggedInUserTeam() {
      return Object.values(this.loggedInUserTeams)[0];
    },
    teamReportingDays() {
      return TimeAndDateConstants.daysOfTheWeek
        .filter(day => this.loggedInUserTeam.standupMeetingDayIds.includes(day.id))
        .map(day => day.weekDayIndex);
    },
    datesForCurrentPage() {
      const minIndex = (this.numberOfPages * this.pageSize) - this.pageSize;
      const maxIndex = this.numberOfPages * this.pageSize;

      return this.dates.slice(minIndex, maxIndex);
    },
    isLoading() {
      return this.areUserGoalsLoading;
    },
    showNonReportingDaysPreference: {
      set(newVal) {
        this.areNonReportingDaysShowing = newVal;

        const updateUserPayload = {
          userId: this.activeUser.id,
          updatedUser: {
            showNonReportingDays: newVal,
          },
          workspaceId: this.activeWorkspace.id,
        };

        this.updateUserProfile(updateUserPayload)
          .then(() => {
          })
          .catch(() => {
            NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_ERROR);
          });
      },
      get() {
        return this.activeUser.showNonReportingDays;
      },
    },
  },

  created() {
    this.fetchInitialData();
  },

  watch: {
    '$route'() { // eslint-disable-line object-shorthand
      this.fetchInitialData();
    },
  },

  methods: {
    generatePastDatesInISO,
    isObjectEmpty,
    isSameDate,
    dateToISO,
    parseDate,
    isSameDayOfTheWeek,
    ...mapActions({
      updateGoal: GoalActions.UPDATE_GOAL,
      fetchGoalsByUserIdsByDates: GoalActions.FETCH_GOALS_BY_USER_IDS_BY_DATES,
      updateUserProfile: UserActions.UPDATE_USER_PROFILE,
    }),
    async handleLoadMoreGoals() {
      this.isNextPageLoading = true;
      this.numberOfPages += 1;
      await this.fetchGoalsByUserIdsByDates({
        userIds: [this.activeUser.id],
        dates: [...this.datesForCurrentPage],
        workspaceId: this.activeWorkspace.id,
      });
      this.isNextPageLoading = false;
    },
    isReportingDay(tableRow, tableRowIndex) {
      return tableRow
        ? this.teamReportingDays.includes(parseDate(tableRow.goalDate).getDay())
        : this.teamReportingDays.includes(parseDate(this.dates[tableRowIndex]).getDay());
    },
    getRowClasses(row, index) {
      let rowClasses = '';

      if (!this.isReportingDay(row, index)) rowClasses = 'has-background-white-ter';

      if (!row || !row.goalStatus || row.goalStatus === Types.GOAL_UNMARKED) {
        // PLACEHOLDER
      } else if (row.goalStatus === Types.GOAL_HIT) {
        rowClasses = rowClasses ? rowClasses.concat(' goal-row--is-success') : 'goal-row--is-success';
      } else if (row.goalStatus === Types.GOAL_MISS) {
        rowClasses = rowClasses ? rowClasses.concat(' goal-row--is-danger') : 'goal-row--is-danger';
      } else if (row.goalStatus === Types.GOAL_CHANGED) {
        rowClasses = rowClasses ? rowClasses.concat(' goal-row--is-warning') : 'goal-row--is-warning';
      }

      return rowClasses;
    },
    async fetchInitialData() {
      this.areUserGoalsLoading = true;
      await this.fetchGoalsByUserIdsByDates({
        userIds: [this.activeUser.id],
        dates: [...this.datesForCurrentPage],
        workspaceId: this.activeWorkspace.id,
      });
      this.areUserGoalsLoading = false;
    },
    handleUpdateGoalStatus(goalStatusPayload) {
      const { goalId, goalStatusExplanation } = goalStatusPayload;
      this.isGoalDetailModalOpen = false;

      this.updateGoal({ goalId, updatedGoal: { goalStatusExplanation } });
    },
  },
};
</script>

<style lang="scss">
.goal-row {
  &--is-success {
    td:first-child {
      border-left: 4px solid $success;
    }
  }

  &--is-warning {
    td:first-child {
      border-left: 4px solid $warning;
    }
  }

  &--is-danger {
    td:first-child {
      border-left: 4px solid $danger;
    }
  }
}
</style>
